/*
** $Id$
**
** Copyright 2018 Port25 Solutions, Inc.
** All rights reserved.
*/


import React, { Component } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import '../css/table.css';
import PropTypes from 'prop-types';
import "bootstrap/dist/css/bootstrap.min.css";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css"
import paginationFactory from 'react-bootstrap-table2-paginator';


// We don't use the default locale (from browser) so as to keep the interface
// consistent for users, and in sync with the output formatting of PowerMTA
// commands (i.e. those run with 'pmta' binary).
let numberFormatter = new Intl.NumberFormat('en-US')

// Number of rows per page (for pagination)
const MAX_ROWS_PER_PAGE = 10


/**
 * A data table.
 */
export default class Table extends Component {
    constructor(props) {
        super(props)
        this.doSort = this.doSort.bind(this)
        this.makePaginationFactory = this.makePaginationFactory.bind(this)
    }


    static propTypes = {
        columns: PropTypes.array.isRequired,
        data: PropTypes.arrayOf(PropTypes.array).isRequired,
        columnFormatter: PropTypes.object,
        caption: PropTypes.string,
        hideColumnTitles : PropTypes.bool,
        columnClasses : PropTypes.func,
        sort : PropTypes.bool
    };


    /**
     * Returns the sort order for given values.
     * 
     * The default sort function from 'react-bootstrap-table-next' treats all values as strings.
     * This breaks sorting for columns that hold numbers such as "#Rcpt".  This function
     * overcomes the limitation (for sorting purposes it treats a pre-defined list of column
     * names as holding numbers, and anything that is not in that list as holding strings).
     *
     * @param value1
     *      the first value.
     * @param value2
     *      the second value.
     * @param order
     *      will be 'asc' or 'desc', as enforced by 'react-bootstrap-table-next'.
     * @param dataField
     *      name of the column being sorted.
     * @return
     *      <code>-1</code> if value1 is less than value2, <code>1</code> if value1 is
     *      greater than value2, or <code>0</code> if they are equal.
     */
    doSort(value1, value2, order, dataField) {
        let result = 0;
        if (typeof(value1) === 'string') {
            value1 = value1.replace('%', '').replace(',', '');
            value2 = value2.replace('%', '').replace(',', '');
        }

        if (dataField === '#Rcpt' || dataField === '#KBytes' || dataField === '#Conn'||
            dataField === 'Domains'|| dataField === '%Total'|| dataField === 'recipients'||
            dataField === '% total'|| dataField === 'connections' || dataField === '% Rcpts') {
            value1 = Number(value1)
            value2 = Number(value2)
        }

        if (value1 < value2) {
            result = -1;
        }
        else if (value2 < value1) {
            result = 1;
        }
        if (order === 'desc') {
            result *= -1;
        }
        return result;
    }


    addKeyColumn(columns) {
        // BootstrapTable generates a warning when the keyField contains
        // duplicate values: "Encountered two children with the same key...".
        // To fix this we creat a hidden column and have each row generate
        // a unique value for it.
        // See https://github.com/react-bootstrap-table/react-bootstrap-table2/issues/166
        let keyColumn = this.makeColumn('port25Key')
        columns.push(Object.assign(keyColumn, {hidden: true}))
    }


    makeColumn(name) {
        let sort = this.props.sort || false;
        let column = {
            dataField: name,
            text: name,
            headerClasses: 'right',
            sort: sort,
            sortFunc: this.doSort
        }
        if (this.props.columnClasses) {
            column['classes'] = this.props.columnClasses
        }
        return column
    }


    getEmptyMessage() {
        return '(none)'
    }


    render() {
        let columns = []
        for (let i = 0; i < this.props.columns.length; i++) {
            let name = this.props.columns[i]
            let col = this.makeColumn(name)
            if (this.props.columnFormatter && this.props.columnFormatter[name]){
                col["formatter"] = this.props.columnFormatter[name]
            }
            columns.push(col)
        }
        this.addKeyColumn(columns)

        let data = []
        let uniqueKey = 0
        if (this.props.data.length > 0) {
            this.props.data.forEach((row) => {
                if (row.length > 0) {
                    let datum = {}
                    for (let i = 0; i < row.length; i++) {
                        datum[this.props.columns[i]] = this.format(i, row[i])
                    }
                    datum['port25Key'] = uniqueKey++
                    data.push(datum)
                }
            })
        }
        let headerClasses = this.props.hideColumnTitles ? 'hiddenTableHeader'
                                                        : 'tableHeader'
        let pagination = this.props.data.length > MAX_ROWS_PER_PAGE
                                                    ? this.makePaginationFactory()
                                                    : undefined
                          
        return (
            <BootstrapTable striped bordered condensed hover
                bootstrap4
                keyField='port25Key'
                columns={columns}
                data={data}
                rowClasses='right'
                caption={this.props.caption}
                headerClasses={headerClasses}
                noDataIndication={this.getEmptyMessage()}
                id={this.props.id}
                defaultSortDirection='asc'
                pagination={pagination}/>
        );
    }


    makePaginationFactory() {
        let options = {
            sizePerPage: MAX_ROWS_PER_PAGE,
            hideSizePerPage: true
        }
        return this.props.paginate ? paginationFactory(options) : undefined
    }


    format(colIndex, value) {
        // Columns with formatters already know how to format, so skip them.
        let column = this.props.columns[colIndex]
        let isColumnFormatted = this.props.columnFormatter &&
                                this.props.columnFormatter[column]
        if (typeof value === 'number' && !isColumnFormatted) {
            return numberFormatter.format(value)
        }
        return value
    }
}
